// Spotify CSV parser for various export formats
// Use browser-friendly ESM build to avoid Buffer dependency in the browser
import { parse } from 'csv-parse/browser/esm/sync';
import type { 
  ParsedSpotifyData, 
  SpotifySongMetric, 
  SpotifyPlaylistMetric,
  FileValidationResult 
} from '../../shared/types/analytics';
import { deduplicationService } from './deduplication';

export class SpotifyParserService {
  /**
   * Sanitize CSV content to tolerate unescaped inner quotes inside quoted fields.
   * Doubles stray quotes found inside an open quoted field when the following
   * character is neither a quote nor a delimiter/newline.
   */
  private sanitizeCsv(input: string): string {
    const delimiter = ',';
    let out = '';
    let inQuotes = false;
    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      if (ch === '"') {
        const next = input[i + 1];
        if (inQuotes) {
          if (next === '"') {
            // Escaped quote pair, keep both and skip next
            out += '""';
            i++;
            continue;
          } else if (next === delimiter || next === '\n' || next === '\r' || next === undefined) {
            inQuotes = false;
            out += ch;
          } else {
            // stray inner quote
            out += '""';
            continue;
          }
        } else {
          inQuotes = true;
          out += ch;
        }
      } else {
        out += ch;
      }
    }
    return out;
  }
  /**
   * Validate CSV file structure
   */
  validateSpotifyCSV(csvContent: string): FileValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    let estimated_records = 0;

    try {
      const safeContent = this.sanitizeCsv(csvContent);
      // Parse first few rows to detect format
      const lines = safeContent.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        errors.push('CSV file appears to be empty or has no data rows');
        return {
          valid: false,
          errors,
          warnings,
          file_info: { size: csvContent.length, type: 'csv' }
        };
      }

      estimated_records = lines.length - 1; // Subtract header

      // Parse header to detect format
      const firstRow = lines[0].toLowerCase();
      
      // Detect format type
      let detectedFormat: string | null = null;
      
      if ((firstRow.includes('song') || firstRow.includes('track')) && firstRow.includes('stream')) {
        detectedFormat = 'songs';
        
        // Check for required song columns
        if (!firstRow.includes('streams')) {
          errors.push('Missing required column: Streams');
        }
        if (!firstRow.includes('listeners')) {
          warnings.push('Missing recommended column: Listeners (will default to 0)');
        }
      } else if ((firstRow.includes('playlist') || firstRow.includes('owner') || firstRow.includes('author') || (firstRow.includes('title') && firstRow.includes('author'))) && firstRow.includes('stream')) {
        detectedFormat = 'playlists';
        
        // Check for required playlist columns
        if (!firstRow.includes('streams')) {
          errors.push('Missing required column: Streams');
        }
        // No warning for Followers: Break Records playlist spec does not include it by default
      } else if (firstRow.includes('date') && firstRow.includes('streams')) {
        detectedFormat = 'timeline';
        if (!firstRow.includes('date')) {
          errors.push('Missing required column: date');
        }
        if (!firstRow.includes('streams')) {
          errors.push('Missing required column: streams');
        }
      } else {
        errors.push('Unable to detect Spotify CSV format. Expected "songs", "playlists", or "timeline" data with a "streams" column.');
      }

      // Validate sample data rows
      if (lines.length > 1) {
        try {
          const records = parse(safeContent, {
            columns: true,
            skip_empty_lines: true,
            trim: true,
            relax_quotes: true, // Allow unescaped quotes
            relax_column_count: true, // Allow varying column counts
            skip_lines_with_error: true,
            bom: true
          });

          if (records.length === 0) {
            errors.push('No valid data rows found in CSV');
          } else if (records.length > 10000) {
            warnings.push(`Large file with ${records.length} records - processing may take longer`);
          }

          // Validate data types in sample records
          const sampleSize = Math.min(5, records.length);
          for (let i = 0; i < sampleSize; i++) {
            const record = records[i] as Record<string, any>;
            
            if (detectedFormat === 'songs') {
              if (record['streams'] && isNaN(Number(record['streams']))) {
                errors.push(`Row ${i + 2}: Streams value "${record['streams']}" is not a valid number`);
              }
              if (record['listeners'] && isNaN(Number(record['listeners']))) {
                errors.push(`Row ${i + 2}: Listeners value "${record['listeners']}" is not a valid number`);
              }
            } else if (detectedFormat === 'playlists') {
              if (record['streams'] && isNaN(Number(record['streams']))) {
                errors.push(`Row ${i + 2}: Streams value "${record['streams']}" is not a valid number`);
              }
              if (record['followers'] && isNaN(Number(record['followers']))) {
                errors.push(`Row ${i + 2}: Followers value "${record['followers']}" is not a valid number`);
              }
            } else if (detectedFormat === 'timeline') {
              if (record['date'] && !/^\d{4}-\d{2}-\d{2}$/.test(record['date'])) {
                errors.push(`Row ${i + 2}: Date value "${record['date']}" is not in YYYY-MM-DD format`);
              }
              if (record['streams'] && isNaN(Number(record['streams']))) {
                errors.push(`Row ${i + 2}: Streams value "${record['streams']}" is not a valid number`);
              }
            }
          }
        } catch (parseError) {
          errors.push(`CSV parsing error: ${parseError}`);
        }
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings,
        file_info: {
          size: csvContent.length,
          type: 'csv',
          estimated_records
        }
      };
    } catch (error) {
      return {
        valid: false,
        errors: [`Validation error: ${error}`],
        warnings,
        file_info: { size: csvContent.length, type: 'csv' }
      };
    }
  }

  /**
   * Detect CSV period type from filename or content
   */
  detectPeriodType(filename: string, csvContent: string): '1day' | '7day' | '28day' | 'all' {
    const nameLower = filename.toLowerCase();
    
    if (nameLower.includes('1day') || nameLower.includes('1-day') || nameLower.includes('daily')) {
      return '1day';
    } else if (nameLower.includes('7day') || nameLower.includes('7-day') || nameLower.includes('weekly')) {
      return '7day';
    } else if (nameLower.includes('28day') || nameLower.includes('28-day') || nameLower.includes('monthly')) {
      return '28day';
    } else if (nameLower.includes('alltime') || nameLower.includes('all-time') || nameLower.includes('lifetime')) {
      return 'all';
    }

    // Try to detect from content patterns
    const lines = csvContent.split('\n');
    const recordCount = lines.length - 1;
    
    // Heuristics based on typical data volume
    if (recordCount < 50) {
      return '1day';
    } else if (recordCount < 200) {
      return '7day';
    } else if (recordCount < 500) {
      return '28day';
    } else {
      return 'all';
    }
  }

  /**
   * Parse date range from CSV content
   */
  parseDateRange(csvContent: string, period_type: string): { start: string; end: string } {
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    // Try to find date information in CSV headers or content
    const lines = csvContent.split('\n').slice(0, 5); // Check first few lines
    const datePattern = /(\d{4}[-\/]\d{2}[-\/]\d{2})/g;
    const foundDates: string[] = [];

    for (const line of lines) {
      const matches = line.match(datePattern);
      if (matches) {
        foundDates.push(...matches);
      }
    }

    if (foundDates.length >= 2) {
      const dates = foundDates.map(d => new Date(d)).sort((a, b) => a.getTime() - b.getTime());
      return {
        start: formatDate(dates[0]),
        end: formatDate(dates[dates.length - 1])
      };
    }

    // Fallback to period-based calculation
    const endDate = today;
    let startDate: Date;

    switch (period_type) {
      case '1day':
        startDate = new Date(today.getTime() - 1 * 24 * 60 * 60 * 1000);
        break;
      case '7day':
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '28day':
        startDate = new Date(today.getTime() - 28 * 24 * 60 * 60 * 1000);
        break;
      case 'all':
      default:
        startDate = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000); // 1 year ago
        break;
    }

    return {
      start: formatDate(startDate),
      end: formatDate(endDate)
    };
  }

  /**
   * Parse Spotify songs CSV
   */
  async parseSpotifySongsCSV(
    csvContent: string,
    filename: string,
    artist_id?: string,
    mapping?: { fields?: Record<string, string>; columns?: { include?: Record<string, boolean>; excludedRowIndices?: number[] } }
  ): Promise<ParsedSpotifyData> {
    try {
      const safe = this.sanitizeCsv(csvContent);
      const records = parse(safe, {
        columns: true,
        skip_empty_lines: true,
        trim: true,
        relax_quotes: true,
        relax_column_count: true,
        skip_lines_with_error: true,
        bom: true
      });

      const period_type = this.detectPeriodType(filename, csvContent);
      const date_range = this.parseDateRange(csvContent, period_type);

      const songs: Array<Omit<SpotifySongMetric, 'id' | 'upload_id' | 'created_at'>> = [];

      let rowIndex = 0;
      const excluded = new Set(mapping?.columns?.excludedRowIndices || []);
      for (const rawRecord of records) {
        if (excluded.has(rowIndex++)) continue;
        const record = rawRecord as Record<string, any>;

        // Mapping-aware accessors
        const includeMap = mapping?.columns?.include || {} as Record<string, boolean>;
        const get = (key: string, ...alts: string[]) => {
          const m = mapping?.fields?.[key];
          if (m && record[m] !== undefined) {
            if (includeMap[m] === false) return undefined;
            return record[m];
          }
          for (const a of alts) {
            if (record[a] !== undefined) {
              if (includeMap[a] === false) continue;
              return record[a];
            }
          }
          return undefined;
        };

        const songName = get('song', 'Song Name', 'song_name', 'track', 'title');
        const streams = parseInt(get('streams', 'Streams', 'streams') || '0', 10);
        const listeners = parseInt(get('listeners', 'Listeners', 'listeners', 'unique_listeners') || '0', 10);
        const saves = parseInt(get('saves', 'Saves', 'saves', 'saved') || '0', 10);
        const playlistAdds = parseInt(get('playlist_adds', 'Playlist Adds', 'playlist_adds', 'added_to_playlists') || '0', 10);
        const skipRate = parseFloat(get('skip_rate', 'Skip Rate', 'skip_rate', 'skips') || '0');
        const completionRate = parseFloat(get('completion_rate', 'Completion Rate', 'completion_rate', 'completions') || '0');
        const isrc = get('isrc', 'ISRC', 'isrc', 'track_isrc');
        const releaseDate = get('release_date', 'Release Date', 'release_date', 'published');

        if (!songName) {
          console.warn('Skipping record missing song name:', record);
          continue;
        }

        const songData = {
          artist_id,
          song_name: songName,
          isrc: isrc || undefined,
          release_date: releaseDate ? new Date(releaseDate).toISOString().split('T')[0] : undefined,
          period_type,
          listeners,
          streams,
          saves,
          playlist_adds: playlistAdds,
          skip_rate: skipRate,
          completion_rate: completionRate,
          recorded_at: date_range.end,
          data_hash: '' // Will be generated by deduplication service
        };

        // Filter: do not import before release date (allow day before)
        if (songData.release_date) {
          const rd = new Date(songData.release_date);
          const cutoff = new Date(rd.getTime() - 24 * 60 * 60 * 1000);
          const rec = new Date(songData.recorded_at);
          if (rec < cutoff) {
            continue;
          }
        }

        // Generate hash for deduplication
        songData.data_hash = deduplicationService.generateDataHash(songData, 'spotify', 'song');
        songs.push(songData);
      }

      return {
        songs,
        playlists: [],
        period_type,
        date_range
      };
    } catch (error) {
      throw new Error(`Failed to parse Spotify songs CSV: ${error}`);
    }
  }

  /**
   * Parse Spotify playlists CSV
   */
  async parseSpotifyPlaylistsCSV(
    csvContent: string,
    filename: string,
    artist_id?: string,
    mapping?: { fields?: Record<string, string>; columns?: { include?: Record<string, boolean>; excludedRowIndices?: number[] } }
  ): Promise<ParsedSpotifyData> {
    try {
      const safe = this.sanitizeCsv(csvContent);
      const records = parse(safe, {
        columns: true,
        skip_empty_lines: true,
        trim: true,
        relax_quotes: true,
        relax_column_count: true,
        skip_lines_with_error: true,
        bom: true
      });

      const period_type = this.detectPeriodType(filename, csvContent);
      const date_range = this.parseDateRange(csvContent, period_type);

      const playlists: Array<Omit<SpotifyPlaylistMetric, 'id' | 'upload_id' | 'created_at'>> = [];

      let rowIndex = 0;
      const excluded = new Set(mapping?.columns?.excludedRowIndices || []);
      for (const rawRecord of records) {
        if (excluded.has(rowIndex++)) continue;
        const record = rawRecord as Record<string, any>;

        const includeMap = mapping?.columns?.include || {} as Record<string, boolean>;
        const get = (key: string, ...alts: string[]) => {
          const m = mapping?.fields?.[key];
          if (m && record[m] !== undefined) {
            if (includeMap[m] === false) return undefined;
            return record[m];
          }
          for (const a of alts) {
            if (record[a] !== undefined) {
              if (includeMap[a] === false) continue;
              return record[a];
            }
          }
          return undefined;
        };

        const playlistName = get('title', 'Playlist Name', 'playlist_name', 'name');
        const streams = parseInt(get('streams', 'Streams', 'streams') || '0', 10);
        const listeners = parseInt(get('listeners', 'Listeners', 'listeners') || '0', 10);
        const followers = parseInt(get('followers', 'Followers', 'followers', 'subscriber_count') || '0', 10);
        const ownerName = get('author', 'Owner', 'owner_name', 'curator');
        const playlistType = (get('playlist_type', 'Type', 'playlist_type', 'category') || 'user') as string;
        const dateAdded = get('date_added', 'Date Added', 'date_added');

        if (!playlistName) {
          console.warn('Skipping record missing playlist name:', record);
          continue;
        }

        // Normalize playlist type
        let normalizedType: 'user' | 'editorial' | 'algorithmic' | 'artist' = 'user';
        if (playlistType.toLowerCase().includes('editorial')) {
          normalizedType = 'editorial';
        } else if (playlistType.toLowerCase().includes('algorithmic') || playlistType.toLowerCase().includes('algorithm')) {
          normalizedType = 'algorithmic';
        } else if (playlistType.toLowerCase().includes('artist')) {
          normalizedType = 'artist';
        }

        const playlistData = {
          artist_id,
          playlist_name: playlistName,
          owner_name: ownerName || undefined,
          playlist_type: normalizedType,
          followers,
          streams,
          listeners,
          period_type,
          recorded_at: dateAdded ? new Date(dateAdded).toISOString().split('T')[0] : date_range.end,
          data_hash: '' // Will be generated by deduplication service
        };

        // Generate hash for deduplication
        playlistData.data_hash = deduplicationService.generateDataHash(playlistData, 'spotify', 'playlist');
        playlists.push(playlistData);
      }

      return {
        songs: [],
        playlists,
        period_type,
        date_range
      };
    } catch (error) {
      throw new Error(`Failed to parse Spotify playlists CSV: ${error}`);
    }
  }

  /**
   * Parse Spotify timeline CSV (date-based streams data)
   */
  async parseSpotifyTimelineCSV(
    csvContent: string,
    filename: string,
    artist_id?: string,
    mapping?: { fields?: Record<string, string>; columns?: { include?: Record<string, boolean>; excludedRowIndices?: number[] } }
  ): Promise<ParsedSpotifyData> {
    try {
      const records = parse(csvContent, {
        columns: true,
        skip_empty_lines: true,
        trim: true,
        relax_quotes: true,
        relax_column_count: true
      });

      const period_type = this.detectPeriodType(filename, csvContent);
      const date_range = this.parseDateRange(csvContent, period_type);

      // Extract song name from filename (e.g., "SKIN-timeline.csv" -> "SKIN")
      const songName = filename.replace(/[-_]timeline\.csv$/i, '').replace(/[-_]/g, ' ');

      const songs: Array<Omit<SpotifySongMetric, 'id' | 'upload_id' | 'created_at'>> = [];

      let rowIndex = 0;
      const excluded = new Set(mapping?.columns?.excludedRowIndices || []);
      for (const rawRecord of records) {
        if (excluded.has(rowIndex++)) continue;
        const record = rawRecord as Record<string, any>;

        const includeMap = mapping?.columns?.include || {} as Record<string, boolean>;
        const get = (key: string, ...alts: string[]) => {
          const m = mapping?.fields?.[key];
          if (m && record[m] !== undefined) {
            if (includeMap[m] === false) return undefined;
            return record[m];
          }
          for (const a of alts) {
            if (record[a] !== undefined) {
              if (includeMap[a] === false) continue;
              return record[a];
            }
          }
          return undefined;
        };

        const date = get('date', 'date', 'Date');
        const streams = parseInt(get('streams', 'streams', 'Streams') || '0', 10);
        const relDate = get('release_date', 'Release Date', 'release_date', 'published');

        if (!date || streams === 0) {
          continue; // Skip rows with no date or zero streams
        }

        const songData = {
          artist_id,
          song_name: songName,
          isrc: undefined,
          release_date: undefined,
          period_type: '1day' as const, // Timeline data is daily
          listeners: 0, // Not available in timeline data
          streams,
          saves: 0,
          playlist_adds: 0,
          skip_rate: 0,
          completion_rate: 0,
          recorded_at: date,
          data_hash: '' // Will be generated by deduplication service
        };

        // Release date filtering for timeline
        if (relDate) {
          const rd = new Date(relDate);
          const cutoff = new Date(rd.getTime() - 24 * 60 * 60 * 1000);
          const rec = new Date(date);
          if (rec < cutoff) {
            continue;
          }
        }

        // Generate hash for deduplication
        songData.data_hash = deduplicationService.generateDataHash(songData, 'spotify', 'song');
        songs.push(songData);
      }

      return {
        songs,
        playlists: [],
        period_type: '1day',
        date_range
      };
    } catch (error) {
      throw new Error(`Failed to parse Spotify timeline CSV: ${error}`);
    }
  }

  /**
   * Auto-detect and parse any Spotify CSV format
   */
  async parseSpotifyCSV(
    csvContent: string,
    filename: string,
    artist_id?: string,
    mapping?: { fields?: Record<string, string> }
  ): Promise<ParsedSpotifyData> {
    const validation = this.validateSpotifyCSV(csvContent);
    
    if (!validation.valid) {
      throw new Error(`CSV validation failed: ${validation.errors.join(', ')}`);
    }

    // Auto-detect format from header
    const firstRow = csvContent.split('\n')[0].toLowerCase();
    
    if (firstRow.includes('playlist') || firstRow.includes('owner') || firstRow.includes('author') || (firstRow.includes('title') && firstRow.includes('author'))) {
      return this.parseSpotifyPlaylistsCSV(csvContent, filename, artist_id, mapping);
    } else if (firstRow.includes('date') && firstRow.includes('streams')) {
      return this.parseSpotifyTimelineCSV(csvContent, filename, artist_id, mapping);
    } else {
      return this.parseSpotifySongsCSV(csvContent, filename, artist_id, mapping);
    }
  }
}

export const spotifyParserService = new SpotifyParserService();
